import { Group, ScrollArea, Stack, Title } from '@mantine/core';
import { DatePickerInput, DatesProvider } from '@mantine/dates';

import { IconCalendarDue } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../shared/components/UI/Button/Button';
import { Loader } from '../../../../../../shared/components/UI/Loader/Loader';
import { SidepanelHeader } from '../../../../../../shared/components/UI/Sidepanel/Sidepanel';

import { PropertiesField } from './components/propertiesField/PropertiesField';
import type { TenderAutoCompleteData } from './components/tenderAutocomplete/tenderAutocomplete';
import { TenderAutocomplete } from './components/tenderAutocomplete/tenderAutocomplete';
import { useCreateOrEditResponseProjectForm } from './hooks/useCreateOrEditResponseProjectForm';
import type { UseCreateOrEditResponseProjectPanelProps } from './hooks/useCreateOrEditResponseProjectPanel';

interface CreateOrEditResponseProjectPanelProps
  extends UseCreateOrEditResponseProjectPanelProps {
  closePanel: () => void;
}

const PANEL_WIDTH = 755;

export function CreateOrEditResponseProjectPanel({
  closePanel,
  id,
}: CreateOrEditResponseProjectPanelProps) {
  const isEditMode = !!id;

  return (
    <Stack w={PANEL_WIDTH} style={{ flex: 1 }} spacing="04">
      <Header closePanel={closePanel} isEditMode={isEditMode} />
      <Content closePanel={closePanel} isEditMode={isEditMode} />
    </Stack>
  );
}

interface HeaderProps
  extends Pick<CreateOrEditResponseProjectPanelProps, 'closePanel'> {
  isEditMode: boolean;
}

function Header({ closePanel, isEditMode }: HeaderProps) {
  const { t } = useTranslation('bidResponse');
  const titleContent = t(
    `project.createOrEditProject.${isEditMode ? 'edit' : 'create'}.title`,
  );

  return (
    <SidepanelHeader
      header={<Title order={4}>{titleContent}</Title>}
      closePanel={closePanel}
    />
  );
}

interface ContentProps {
  closePanel: () => void;
  isEditMode: boolean;
}

function Content({ closePanel, isEditMode }: ContentProps) {
  const { t } = useTranslation('bidResponse');

  const submitButtonContent = t(
    `project.createOrEditProject.${isEditMode ? 'edit' : 'create'}.submitButton`,
  );

  const cancelButtonContent = t(
    `project.createOrEditProject.${isEditMode ? 'edit' : 'create'}.cancelButton`,
  );

  const { tender, properties, onSubmit, form, isLoading } =
    useCreateOrEditResponseProjectForm({ closePanel });

  function handleTenderChange(t: TenderAutoCompleteData | undefined) {
    if (!t) {
      tender.update(undefined);
      return;
    }

    tender.update({
      id: t.id,
      title: t.title,
      buyerId: t.buyerId,
      buyerName: t.buyerName,
      responseDeadline: t.responseDeadline,
    });
  }

  function handlePropertiesChange(p: number[]) {
    properties.update(p);
  }

  return (
    <form onSubmit={onSubmit} style={{ flex: 1 }}>
      <Wrapper>
        <Stack
          spacing="05"
          w={`calc(${PANEL_WIDTH}px - (24px * 2))`}
          sx={{ overflowX: 'hidden' }}
        >
          <TenderAutocomplete
            error={tender.error}
            selectedTender={tender.value}
            onTenderChange={handleTenderChange}
          />
          <DatesProvider settings={{ locale: 'fr' }}>
            <DatePickerInput
              clearable
              icon={<IconCalendarDue size={16} />}
              radius="md"
              valueFormat="DD MMMM YYYY"
              label={t(
                'project.createOrEditProject.responseDeadlineField.label',
              )}
              placeholder={t(
                'project.createOrEditProject.responseDeadlineField.placeholder',
              )}
              w="fit-content"
              p="10px, 16px, 10px, 16px"
              {...form.getInputProps('responseDeadline')}
            />
          </DatesProvider>
          <PropertiesField
            selectedProperties={properties.value}
            onPropertiesChange={handlePropertiesChange}
          />
        </Stack>
        <Group
          position="apart"
          align="center"
          p="05"
          sx={theme => ({ borderTop: `1px solid ${theme.colors.gray[0]}` })}
        >
          {isLoading && <Loader loaderProps={{ size: 'sm' }} />}
          <Group position="right" sx={{ flex: 1 }} spacing={'03'}>
            <Button variant="outline" onClick={closePanel}>
              {cancelButtonContent}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {submitButtonContent}
            </Button>
          </Group>
        </Group>
      </Wrapper>
    </form>
  );
}

function Wrapper({
  children,
}: {
  children: [React.ReactNode, React.ReactNode];
}) {
  return (
    <Stack h="100%" style={{ flex: 1 }}>
      <Stack p="05" pt="0" style={{ flex: 1, overflow: 'hidden' }}>
        <ScrollArea h="100%" w="100%">
          {children[0]}
        </ScrollArea>
      </Stack>
      {children[1]}
    </Stack>
  );
}
